import countryLinks from '../data/countryLinks.json';
import guideLinks from '../data/guideLinks.json';
import { withSlash } from '../seo/urls';
import React from 'react';
import { useLocation } from 'react-router-dom';
import { SlashLink as Link } from './SlashLink';
import { QUESTION_BANK_LABEL, SESSION_MOCK_SHORT } from '../constants/product';
import { freeTrialPublicLine, planPricingPublicLine } from '../constants/pricing';
import useSiteSettings from '../hooks/useSiteSettings';
import useAccess from '../hooks/useAccess';
import { dashboardPathFromAccess } from '../utils/memberHome';
import { COMPANY_LEGAL_NAME } from '../constants/company';

/** CFA-25: Free Mock 1 footer link should match the level page the visitor is on. */
export function freeMockFooterLink(pathname = '') {
  const path = String(pathname || '');
  if (path.startsWith('/level/2') || path.startsWith('/quiz/l2') || path.startsWith('/l2')) {
    return { to: '/quiz/l2/session-1', label: 'Free Mock 1' };
  }
  if (path.startsWith('/level/3') || path.startsWith('/quiz/l3') || path.startsWith('/l3')) {
    return { to: '/quiz/l3/session-1', label: 'Free Mock 1' };
  }
  return { to: '/blog/free-cfa-level-1-mock-exam-2026', label: 'Free Mock 1' };
}

export default function SiteFooter() {
  const { settings } = useSiteSettings();
  const access = useAccess();
  const { pathname } = useLocation();
  const showPricing = !!settings.monetizationEnabled;
  const dashboardPath = access.user ? dashboardPathFromAccess(access) : '/login?next=/dashboard';
  const freeMock = freeMockFooterLink(pathname);

  return (
    <footer className="site-footer">
      <div className="site-footer-inner">
        <div className="site-footer-brand">
          <span className="site-footer-mark">CFA Mock Exam</span>
          <p>Computer-based CFA® Level 1–3 practice for the 2026 curriculum.</p>
          <ul className="site-footer-facts">
            <li>{SESSION_MOCK_SHORT} Level 1 timed session · {QUESTION_BANK_LABEL} item bank</li>
            <li>{showPricing ? freeTrialPublicLine() : 'Official topic weights and an instant score report'}</li>
            {showPricing && planPricingPublicLine().split('. ').map((line) => <li key={line}>{line}</li>)}
          </ul>
        </div>
        <div className="site-footer-links">
          <nav className="site-footer-nav" aria-label="Practice">
            <h2>Practice</h2>
            <Link to={freeMock.to}>{freeMock.label}</Link>
            <Link to="/cfa-mock-exams/">All CFA mock exams</Link>
            <Link to="/quiz/session">Level 1 mocks</Link>
            <Link to="/level/2">Level 2 mocks</Link>
            <Link to="/level/3">Level 3 mocks</Link>
            {showPricing && <Link to="/pricing">Pricing</Link>}
            <Link to={dashboardPath}>Dashboard</Link>
          </nav>
          <nav className="site-footer-nav" aria-label="Resources">
            <h2>Resources</h2>
            <Link to={access.paid || access.isAdmin ? '/study-guide' : '/#topics'}>Topics</Link>
            <Link to="/study-guide">Study guide</Link>
            <Link to="/glossary">Glossary</Link>
            <Link to="/blog">Blog</Link>
            <Link to="/blog/cfa-level-1-exam-dates-2026">Nov 2026 dates</Link>
            {guideLinks.map(({ path, name }) => <Link key={path} to={withSlash(path)}>{name}</Link>)}
          </nav>
          <nav className="site-footer-nav" aria-label="CFA exam guides by country"><h2>CFA exam guides by country</h2><ul>{countryLinks.map(({ path, name }) => <li key={path}><Link to={withSlash(path)}>CFA exam guide: {name}</Link></li>)}</ul></nav>
          <nav className="site-footer-nav" aria-label="Company">
            <h2>Company</h2>
            <Link to="/about">About</Link>
            <Link to="/contact">Contact</Link>
            <Link to="/privacy">Privacy</Link>
            <Link to="/terms">Terms</Link>
          </nav>
        </div>
      </div>
      <p className="site-footer-legal">
        CFA® and Chartered Financial Analyst® are trademarks owned by CFA Institute.
        This site is not affiliated with, endorsed by, or sponsored by CFA Institute.
        Practice questions are original study material and are not official CFA
        Institute exam items.
      </p>
      <p className="site-footer-legal">
        © {new Date().getFullYear()} {COMPANY_LEGAL_NAME}. All rights reserved.
      </p>
    </footer>
  );
}
